import { useEffect } from 'react'
import ReactCountryFlag from 'react-country-flag'
import useI18n from '@/shared/hooks/usei18n'
import {
  ImportedGood,
  useGoodsReceivedReportDownloadUrl
} from '@/features/cbam-reporting/hooks/importedGoods'
import { trackCbamReportingSignal } from '@/features/cbam-reporting/analytics/cbamReportingClarity'
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, H2 } from '@/common'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Importer } from '@/features/importers/hooks/importers'
import { Installation } from '@/features/suppliers/hooks/installations'
import { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import { ImportedGoodEligibilityResult } from '@/shared/utils/importedGoodEligibility'
import { PreviousProcedures, RequestedProcedures } from '@/config/customsProcedure'
import CbamGoodEmissionsProjectionSection from '@/shared/components/cbam-good-details/CbamGoodEmissionsProjectionSection'
import CbamGoodCalculationMethodSection from '@/shared/components/cbam-good-details/CbamGoodCalculationMethodSection'
import CbamGoodDetailsDrawerLayout from '@/shared/components/cbam-good-details/CbamGoodDetailsDrawerLayout'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import useDialog from '@/common/hooks/useDialog'
import ImportedGoodLiabilityOverviewDialog from './liability-explanation/ImportedGoodLiabilityOverviewDialog'

const formatValue = (value: string | undefined | null) => value || '-'

const getCustomsProcedureName = (
  code: string | undefined | null,
  procedures: Array<{ code: string; value: string }>
): string | null => {
  if (!code) {
    return null
  }
  const procedure = procedures.find(proc => proc.code === code)
  return procedure?.value || null
}

export default function ImportedGoodDetailsDrawer({
  isOpen,
  close,
  importedGood,
  goodsReceivedReportFileName,
  organizationId,
  cbamReportId,
  installation,
  supplier,
  importer,
  liabilityData,
  supplierEmissionsReport,
  eligibility,
  isRepresentative,
  reportingPeriodYear,
  modifiers,
  aggregatedGoodsCategory
}: {
  isOpen: boolean
  close: () => void
  importedGood: ImportedGood | null
  goodsReceivedReportFileName: string | null
  organizationId: string
  cbamReportId: string
  installation: Installation | null
  supplier: Partnership | null
  importer: Importer | null
  liabilityData: BatchLiabilityResultItem | null
  supplierEmissionsReport: SupplierEmissionsReport | null
  eligibility: ImportedGoodEligibilityResult
  isRepresentative: boolean
  reportingPeriodYear: number
  modifiers: CbamModifiers
  aggregatedGoodsCategory?: string
}) {
  const { t, dateFormatter } = useI18n()
  const { data: goodsReceivedReportDownload, isError: isGoodsReceivedReportDownloadError } =
    useGoodsReceivedReportDownloadUrl(
      organizationId,
      cbamReportId,
      importedGood?.goodsReceivedReportId,
      isOpen && Boolean(goodsReceivedReportFileName)
    )
  const goodsReceivedReportDownloadUrl = isGoodsReceivedReportDownloadError
    ? ''
    : (goodsReceivedReportDownload?.url ?? '')
  const {
    open: isLiabilityExplanationOpen,
    openDialog: openLiabilityExplanation,
    closeDialog: closeLiabilityExplanation
  } = useDialog()

  const canShowLiabilityExplanation =
    reportingPeriodYear === 2026 && Boolean(liabilityData?.result) && importedGood?.quarter != null

  useEffect(() => {
    if (isOpen && importedGood?.id) {
      trackCbamReportingSignal('reporting:good:inspected')
    }
  }, [isOpen, importedGood?.id])

  if (!importedGood) {
    return null
  }

  return (
    <CbamGoodDetailsDrawerLayout
      isOpen={isOpen}
      close={close}
      title={
        <H2>
          {t('cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.title')}
        </H2>
      }
      headerAccessory={
        <div className="flex items-center gap-2">
          <Badge variant={eligibility.status === 'COMPLETE' ? 'green' : 'yellow'}>
            {t(
              `cbamReporting:cbamReportDetails.reportedGoods.eligibilityStatus.${eligibility.status}`
            )}
          </Badge>
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle
            title={t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.sections.metadata'
            )}
          />
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.source'
                )}
              </div>
              <div>
                {goodsReceivedReportFileName ? (
                  goodsReceivedReportDownloadUrl ? (
                    <a
                      href={goodsReceivedReportDownloadUrl}
                      download={goodsReceivedReportFileName}
                      className="text-blue-500 hover:underline"
                    >
                      {goodsReceivedReportFileName}
                    </a>
                  ) : (
                    goodsReceivedReportFileName
                  )
                ) : (
                  t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.uploadSource.manual'
                  )
                )}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.createdAt'
                )}
              </div>
              <div>{dateFormatter(new Date(importedGood.createdAt))}</div>
            </div>
          </div>
        </CardBody>
      </Card>
      {/* Section 1 - Goods & Parties */}
      <Card>
        <CardHeader>
          <CardTitle
            title={t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.sections.goodsAndParties'
            )}
          />
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.cnCode'
                )}
              </div>
              <div className="font-medium">{importedGood.cnCode}</div>
            </div>
            {importedGood.description && (
              <div>
                <div className="text-sm text-text-secondary mb-1">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.goodsDescription'
                  )}
                </div>
                <div>{formatValue(importedGood.description)}</div>
              </div>
            )}
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.countryOfOrigin'
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <ReactCountryFlag countryCode={importedGood.country} className="shrink-0" svg />
                <span>{t(`common:countriesByIsoAlpha2.${importedGood.country}`)}</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.importDate'
                )}
              </div>
              <div>
                {importedGood.importDate ? dateFormatter(new Date(importedGood.importDate)) : '-'}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.quarter'
                )}
              </div>
              <div>{formatValue(importedGood.quarter ?? undefined)}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.supplier'
                )}
              </div>
              <div>{formatValue(supplier?.name)}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.installation'
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {installation?.country && (
                  <ReactCountryFlag countryCode={installation.country} className="shrink-0" svg />
                )}
                <span>{formatValue(installation?.name)}</span>
              </div>
            </div>
            {importedGood.sellerName && (
              <div>
                <div className="text-sm text-text-secondary mb-1">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.seller'
                  )}
                </div>
                <div>{formatValue(importedGood.sellerName)}</div>
              </div>
            )}
            {importedGood.customId && (
              <div>
                <div className="text-sm text-text-secondary mb-1">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.customId'
                  )}
                </div>
                <div>{formatValue(importedGood.customId)}</div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
      {isRepresentative && (
        <Card>
          <CardHeader>
            <CardTitle
              title={t(
                'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.sections.importer'
              )}
            />
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-text-secondary mb-1">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.importerName'
                  )}
                </div>
                <div>{formatValue(importer?.name)}</div>
              </div>
              <div>
                <div className="text-sm text-text-secondary mb-1">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.importerEoriNumber'
                  )}
                </div>
                <div>{formatValue(importer?.identificationNumber)}</div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Section 2 - Import & Customs */}
      <Card>
        <CardHeader>
          <CardTitle
            title={t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.sections.customs'
            )}
          />
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.previousCustomsProcedure'
                )}
              </div>
              <div>
                {importedGood.previousCustomsProcedure
                  ? (() => {
                      const procedureValue = getCustomsProcedureName(
                        importedGood.previousCustomsProcedure,
                        PreviousProcedures
                      )
                      return procedureValue
                        ? t(`common:customsProcedures.${procedureValue}`)
                        : formatValue(importedGood.previousCustomsProcedure)
                    })()
                  : '-'}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.requestedCustomsProcedure'
                )}
              </div>
              <div>
                {importedGood.requestedCustomsProcedure
                  ? (() => {
                      const procedureValue = getCustomsProcedureName(
                        importedGood.requestedCustomsProcedure,
                        RequestedProcedures
                      )
                      return procedureValue
                        ? t(`common:customsProcedures.${procedureValue}`)
                        : formatValue(importedGood.requestedCustomsProcedure)
                    })()
                  : '-'}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      <CbamGoodEmissionsProjectionSection
        liabilityData={liabilityData}
        projectionYear={reportingPeriodYear}
        netMassInTonnes={importedGood.netMassInTonnes}
        headerAccessory={
          canShowLiabilityExplanation ? (
            <Button variant="subtle" onClick={openLiabilityExplanation}>
              {t('common:liabilityExplanationDialog.open')}
            </Button>
          ) : undefined
        }
      />
      <CbamGoodCalculationMethodSection
        isUsingDefaultEmissions={importedGood.isUsingDefaultEmissions}
        supplierEmissionsReport={supplierEmissionsReport}
      />
      {canShowLiabilityExplanation && liabilityData?.result ? (
        <ImportedGoodLiabilityOverviewDialog
          isOpen={isLiabilityExplanationOpen}
          close={closeLiabilityExplanation}
          importedGood={importedGood}
          liabilityData={liabilityData}
          supplierEmissionsReport={supplierEmissionsReport}
          modifiers={modifiers}
          reportingPeriodYear={reportingPeriodYear}
          aggregatedGoodsCategory={aggregatedGoodsCategory}
        />
      ) : null}
    </CbamGoodDetailsDrawerLayout>
  )
}
